import { handleError } from '@zyno-io/vue-foundation';
import { watch } from 'vue';

import { GlobalState } from './global';
import { SSECoreReconfigureChannel } from './modules/core/sse';
import { SalesSalesCommonConfigApi } from './openapi-clients-generated/sales';
import { SuiteCoreConfigApi } from './openapi-clients-generated/suite';

export function useTenantConfiguration() {
    // Tenant the current configuration was loaded for — a failed reload keeps that tenant's last good config.
    let loadedTenantId: string | null = null;

    const loadTenantConfiguration = async () => {
        if (!GlobalState.tenant.value || !GlobalState.user.value) {
            GlobalState.tenantConfiguration.value = null;
            loadedTenantId = null;
            return;
        }

        const tenantId = GlobalState.tenant.value.id;
        try {
            const [suiteResponse, salesResponse] = await Promise.all([
                SuiteCoreConfigApi.getCoreConfigGetConsumerConfig(),
                SalesSalesCommonConfigApi.getSalesCommonConfigIndex()
            ]);
            if (GlobalState.tenant.value?.id !== tenantId) return;
            // The two services own disjoint configuration keys. The SPA composes the read model at
            // its boundary so Suite never needs a dependency on Sales configuration.
            GlobalState.tenantConfiguration.value = { ...suiteResponse.data, ...salesResponse.data };
            loadedTenantId = tenantId;
        } catch (error) {
            handleError('Failed to load tenant configuration:', error);
            if (GlobalState.tenant.value?.id !== tenantId) return;
            // A failed `reconfigure` reload must not blank config-driven UI (e.g. workstation-scoped
            // parking) — only a first load for this tenant falls back to null.
            if (loadedTenantId !== tenantId) GlobalState.tenantConfiguration.value = null;
        }
    };

    SSECoreReconfigureChannel.use(loadTenantConfiguration);

    watch(
        GlobalState.tenant,
        () => {
            loadTenantConfiguration();
        },
        { immediate: true }
    );
}
